import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { monitoringAPI } from '../../services/api';

// Monitoring v2 performance details: uptime windows, hourly p50/p95 response
// time (48h), timing breakdown, per-location latency and RUM Web Vitals (p75).

interface Vital { p75: number | null; samples: number; rating: 'good' | 'needs_improvement' | 'poor' | null; thresholds: [number, number] | null }
interface PerfData {
  uptime?: { h24: number | null; d7: number | null; d30: number | null };
  hourly_metrics?: { hour: string; checks: number; failed_checks: number; p50_response_time: number | null; p95_response_time: number | null; ttfb_p50: number | null }[];
  timing_breakdown?: { dns_ms: number; connect_ms: number; tls_ms: number; ttfb_ms: number; total_ms: number; samples: number } | null;
  locations?: { location: string; checks: number; avg_ms: number | null; p95_ms: number | null; failed: number }[];
  web_vitals?: { days: number; samples: number; lcp: Vital; inp: Vital; cls: Vital; fcp: Vital; ttfb: Vital; load: Vital };
}

const RATING: Record<string, { label: string; cls: string }> = {
  good: { label: 'Good', cls: 'success' },
  needs_improvement: { label: 'Needs improvement', cls: 'warning' },
  poor: { label: 'Poor', cls: 'danger' },
};

const VITALS: { key: 'lcp' | 'inp' | 'cls' | 'fcp' | 'ttfb'; name: string; desc: string; unit: string }[] = [
  { key: 'lcp', name: 'LCP', desc: 'Largest Contentful Paint', unit: 'ms' },
  { key: 'inp', name: 'INP', desc: 'Interaction to Next Paint', unit: 'ms' },
  { key: 'cls', name: 'CLS', desc: 'Cumulative Layout Shift', unit: '' },
  { key: 'fcp', name: 'FCP', desc: 'First Contentful Paint', unit: 'ms' },
  { key: 'ttfb', name: 'TTFB', desc: 'Time to First Byte', unit: 'ms' },
];

const fmtVital = (v: number | null, unit: string) =>
  v == null ? '—' : unit === 'ms' ? (v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${v} ms`) : v.toFixed(3);
const pct = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(v === 100 ? 0 : 2)}%`);

const PerformanceInsights: React.FC<{ assetId: number; days: number }> = ({ assetId, days }) => {
  const [data, setData] = useState<PerfData | null>(null);

  useEffect(() => {
    let alive = true;
    monitoringAPI.getPerformance(assetId, days)
      .then((r) => { if (alive) setData(r.data.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [assetId, days]);

  if (!data) return null;
  const hourly = (data.hourly_metrics || []).map((h) => ({
    ...h,
    label: new Date(h.hour).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' }),
  }));
  const tb = data.timing_breakdown;
  const wv = data.web_vitals;

  return (
    <>
      {data.uptime && (
        <div className="row g-3 mb-4">
          {([['h24', 'Uptime 24h'], ['d7', 'Uptime 7 days'], ['d30', 'Uptime 30 days']] as const).map(([k, label]) => {
            const v = data.uptime![k];
            return (
              <div className="col-md-4" key={k}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <div className={`h4 mb-0 ${v == null ? 'text-muted' : v >= 99.9 ? 'text-success' : v >= 99 ? 'text-warning' : 'text-danger'}`}>{pct(v)}</div>
                    <small className="text-muted">{label}</small>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white"><h6 className="mb-0">Response time, last 48 hours (p50 / p95)</h6></div>
            <div className="card-body">
              {hourly.length === 0 ? (
                <div className="text-center text-muted py-5 small">No checks recorded yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={hourly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
                    <YAxis tick={{ fontSize: 11 }} unit=" ms" width={70} />
                    <Tooltip formatter={(v: any) => (v == null ? '—' : `${v} ms`)} />
                    <Legend />
                    <Line type="monotone" dataKey="p50_response_time" name="p50" stroke="#0d6efd" dot={false} strokeWidth={2} connectNulls />
                    <Line type="monotone" dataKey="p95_response_time" name="p95" stroke="#fd7e14" dot={false} strokeWidth={2} connectNulls />
                    <Line type="monotone" dataKey="ttfb_p50" name="TTFB p50" stroke="#20c997" dot={false} strokeDasharray="4 3" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white"><h6 className="mb-0">Where time goes (24h average)</h6></div>
            <div className="card-body">
              {!tb ? <div className="text-muted small">No data yet</div> : (
                <ul className="list-unstyled mb-3">
                  {[['DNS lookup', tb.dns_ms], ['TCP connect', tb.connect_ms], ['TLS handshake', tb.tls_ms],
                    ['Server response (TTFB)', Math.max(0, tb.ttfb_ms - tb.dns_ms - tb.connect_ms - tb.tls_ms)],
                    ['Content download', Math.max(0, tb.total_ms - tb.ttfb_ms)]].map(([label, v]) => (
                    <li key={label as string} className="d-flex justify-content-between border-bottom py-1 small">
                      <span>{label}</span><span className="fw-semibold">{v} ms</span>
                    </li>
                  ))}
                  <li className="d-flex justify-content-between py-1 small"><strong>Total</strong><strong>{tb.total_ms} ms</strong></li>
                </ul>
              )}
              {(data.locations || []).length > 0 && (
                <>
                  <div className="small text-muted mb-1">By check location (24h)</div>
                  {(data.locations || []).map((l) => (
                    <div key={l.location} className="d-flex justify-content-between small">
                      <span><i className="bi bi-geo-alt me-1"></i>{l.location}</span>
                      <span>{l.avg_ms != null ? `${l.avg_ms} ms avg · p95 ${l.p95_ms} ms` : '—'}{l.failed ? ` · ${l.failed} failed` : ''}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white d-flex justify-content-between">
          <h6 className="mb-0">Real-user Web Vitals (p75, last {wv?.days || 28} days)</h6>
          <small className="text-muted">{wv?.samples || 0} page views measured</small>
        </div>
        <div className="card-body">
          {!wv || wv.samples === 0 ? (
            <div className="text-muted small">
              No real-user data yet. Web Vitals are collected automatically by the CloudAyn tracking script once visitors load your pages.
            </div>
          ) : (
            <div className="row g-3">
              {VITALS.map((v) => {
                const m = wv[v.key];
                const r = m.rating ? RATING[m.rating] : null;
                return (
                  <div className="col-md" key={v.key}>
                    <div className={`border rounded p-3 h-100 border-${r?.cls || 'light'}`}>
                      <div className="d-flex justify-content-between">
                        <strong>{v.name}</strong>
                        {r && <span className={`badge bg-${r.cls}`}>{r.label}</span>}
                      </div>
                      <div className="h5 my-1">{fmtVital(m.p75, v.unit)}</div>
                      <small className="text-muted d-block">{v.desc}</small>
                      {m.thresholds && (
                        <small className="text-muted">Good ≤ {fmtVital(m.thresholds[0], v.unit)}, poor &gt; {fmtVital(m.thresholds[1], v.unit)}</small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PerformanceInsights;
